/**
 * Product gallery accessibility remediation (WCAG 2.1 AA)
 *
 * Added July 2026 as part of the Simply Dresses WCAG remediation (audit P0-3).
 *
 * Problem: on desktop (min-width 720px) the PDP gallery runs Flickity 3 with
 * watchCSS. Flickity marks off-screen cells aria-hidden="true" but leaves
 * their contents tabbable: the zoom button and the app-injected Smart
 * Wishlist control. Keyboard focus lands on controls that assistive
 * technology cannot see (SC 4.1.2 / 1.3.1). Measured on the draft:
 * 3 hidden cells, 9 tabbable descendants.
 *
 * Fix: watch aria-hidden on the gallery cells and sync tabindex on their
 * focusable descendants. Original tabindex values are preserved so app
 * widgets that set their own (Smart Wishlist uses tabindex="0") are restored
 * exactly. Flickity drops aria-hidden entirely below 720px (watchCSS
 * deactivation), which the observer handles as a plain attribute change.
 */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var SAVED = 'data-a11y-saved-tabindex';

  // NOTE: querySelectorAll, not querySelector. The PDP renders TWO elements
  // with this class: a CSS-hidden no-js <ul> first, then the <div> Flickity
  // actually enhances. Watching only the first match observes the dead copy
  // and neutralizes nothing (caught in draft verification, 2026-07-23).
  var lists = document.querySelectorAll('.product-gallery__media-list');
  if (!lists.length) return;

  function neutralize(cell) {
    cell.querySelectorAll(FOCUSABLE).forEach(function (el) {
      if (!el.hasAttribute(SAVED)) {
        el.setAttribute(SAVED, el.getAttribute('tabindex') === null ? '' : el.getAttribute('tabindex'));
      }
      el.setAttribute('tabindex', '-1');
    });
  }

  function restore(cell) {
    cell.querySelectorAll('[' + SAVED + ']').forEach(function (el) {
      var saved = el.getAttribute(SAVED);
      if (saved === '') {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', saved);
      }
      el.removeAttribute(SAVED);
    });
  }

  function syncCell(cell) {
    if (cell.getAttribute('aria-hidden') === 'true') {
      neutralize(cell);
    } else {
      restore(cell);
    }
  }

  function syncAll() {
    lists.forEach(function (list) {
      list.querySelectorAll('.product-gallery__media-list-item').forEach(syncCell);
    });
  }

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (m.type === 'attributes' && m.target.classList.contains('product-gallery__media-list-item')) {
        syncCell(m.target);
      } else if (m.type === 'childList') {
        // Flickity reparents cells into its viewport/slider wrappers on init,
        // and app widgets inject controls late. Re-sync everything.
        syncAll();
      }
    });
  });

  lists.forEach(function (list) {
    observer.observe(list, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      childList: true,
      subtree: true
    });
  });

  // Initial pass, then again after Flickity's imagesLoaded init window.
  syncAll();
  setTimeout(syncAll, 3000);
})();
